/**
 * Org-aware currency for marketing surfaces (header nav, SEO pages, blog copy).
 * Static copy may still contain ₹ in source; display is localized via formatMoney.
 */

export const TIER_NAV_RESIDENTIAL = [
  { href: '/residential-cleaning-studio-1bhk-kochi', label: 'Studio · 1 BHK', amount: 4799 },
  { href: '/residential-cleaning-2bhk-kochi', label: '2 BHK', amount: 7499 },
  { href: '/residential-cleaning-3bhk-kochi', label: '3 BHK', amount: 9499 },
  { href: '/residential-cleaning-4-5bhk-villa-kochi', label: '4–5 BHK · Villa', amount: 12499 },
] as const;

export const TIER_NAV_COMMERCIAL = [
  { href: '/commercial-cleaning-up-to-500-sq-ft-kochi', label: 'Up to 500 sq ft', amount: 4799 },
  { href: '/commercial-cleaning-500-1000-sq-ft-kochi', label: '500–1000 sq ft', amount: 8499 },
  { href: '/commercial-cleaning-1000-1500-sq-ft-kochi', label: '1000–1500 sq ft', amount: 9499 },
  { href: '/commercial-cleaning-1500-plus-sq-ft-kochi', label: '1500+ sq ft', amount: 12499 },
] as const;

export function formatFromPrice(amount: number, formatMoney: (n: number) => string): string {
  return `from ${formatMoney(amount)}`;
}

export function formatPricePlus(amount: number, formatMoney: (n: number) => string): string {
  return `${formatMoney(amount)}+`;
}

/** Location / service pricing table row with optional numeric from-price. */
export type MarketingPricingRow = {
  service: string;
  fromPrice?: string;
  fromAmount?: number;
  notes?: string;
  /** Replaces `{fromPriceNNNN}` tokens in notes (suffix = amount digits). */
  notesFromAmount?: number;
};

export type MarketingAmountTokens = {
  /** Replaces `{fromPrice}`, `{FromPrice}`, `{amount}`. */
  primary?: number;
  /** Replaces `{fromPriceKey}`, etc. where Key is the map key (e.g. `1499` → `{fromPrice1499}`). */
  extra?: Record<string, number>;
  /** Replaces `{amountMinOrder}`, `{fromPriceMinOrder}`, `{FromPriceMinOrder}`. */
  addonMinimumOrder?: number;
};

export function applyMarketingAmountTokens(
  text: string,
  formatMoney: (n: number) => string,
  tokens: MarketingAmountTokens,
): string {
  let out = text;
  const replaceFor = (amount: number, suffix: string) => {
    const fromPrice = formatFromPrice(amount, formatMoney);
    const FromPrice = fromPrice.charAt(0).toUpperCase() + fromPrice.slice(1);
    const amountStr = formatMoney(amount);
    out = out.replace(new RegExp(`\\{FromPrice${suffix}\\}`, 'g'), FromPrice);
    out = out.replace(new RegExp(`\\{fromPrice${suffix}\\}`, 'g'), fromPrice);
    out = out.replace(new RegExp(`\\{amount${suffix}\\}`, 'g'), amountStr);
  };
  if (tokens.primary != null && Number.isFinite(tokens.primary)) {
    replaceFor(tokens.primary, '');
  }
  if (tokens.extra) {
    for (const [key, val] of Object.entries(tokens.extra)) {
      if (Number.isFinite(val)) replaceFor(val, key);
    }
  }
  if (tokens.addonMinimumOrder != null && Number.isFinite(tokens.addonMinimumOrder)) {
    replaceFor(tokens.addonMinimumOrder, 'MinOrder');
  }
  return out;
}

/** Apply amount tokens then legacy ₹ substitution for marketing copy. */
export function localizeMarketingText(
  text: string,
  formatMoney: (n: number) => string,
  tokens?: MarketingAmountTokens,
): string {
  const withTokens =
    tokens?.primary != null || tokens?.extra ? applyMarketingAmountTokens(text, formatMoney, tokens) : text;
  return localizeMarketingCurrencyInText(withTokens, formatMoney);
}

export function resolveMarketingPricingFromPrice(
  row: MarketingPricingRow,
  formatMoney: (n: number) => string,
): string {
  if (row.fromAmount != null && Number.isFinite(row.fromAmount)) {
    return formatFromPrice(row.fromAmount, formatMoney);
  }
  if (row.fromPrice) {
    return localizeMarketingCurrencyInText(row.fromPrice, formatMoney);
  }
  return '';
}

/** Localize a location/marketing pricing table row (from price + notes tokens). */
export function localizeMarketingPricingTableRow(
  row: MarketingPricingRow,
  formatMoney: (n: number) => string,
  tokens?: MarketingAmountTokens,
): { service: string; fromPrice: string; notes?: string } {
  const noteTokens: MarketingAmountTokens = {
    ...tokens,
    extra: {
      ...tokens?.extra,
      ...(row.notesFromAmount != null ? { [String(row.notesFromAmount)]: row.notesFromAmount } : {}),
    },
  };
  const fromPrice = resolveMarketingPricingFromPrice(row, formatMoney);
  const notes = row.notes ? localizeMarketingText(row.notes, formatMoney, noteTokens) : undefined;
  return { service: row.service, fromPrice, notes };
}

/** Hero eyebrow: "Compact homes · from {amount}". */
export function formatEyebrowWithFrom(
  prefix: string,
  fromAmount: number,
  formatMoney: (n: number) => string,
): string {
  return `${prefix} · ${formatFromPrice(fromAmount, formatMoney)}`;
}

/** Sentence-leading from-price, e.g. "From ₹4,799. Book online…". */
export function formatLeadingFromAmount(
  fromAmount: number,
  formatMoney: (n: number) => string,
  suffix?: string,
): string {
  const fromPhrase = formatFromPrice(fromAmount, formatMoney);
  const lead = fromPhrase.charAt(0).toUpperCase() + fromPhrase.slice(1);
  return suffix ? `${lead}. ${suffix}` : lead;
}

/**
 * Replace legacy ₹ amounts in marketing strings with org-formatted money.
 * Handles ₹1,234, ₹42K, and ₹1.2L patterns.
 */
export function localizeMarketingCurrencyInText(
  text: string,
  formatMoney: (amount: number) => string,
): string {
  if (!text || !text.includes('₹')) return text;

  let out = text.replace(/₹\s*([\d.]+)\s*L/gi, (_, lakh) => {
    const amount = parseFloat(lakh) * 100000;
    if (!Number.isFinite(amount)) return `₹${lakh}L`;
    return formatMoney(amount);
  });

  out = out.replace(/₹\s*([\d,]+(?:\.\d+)?)\s*K/gi, (_, raw) => {
    const base = Number(String(raw).replace(/,/g, ''));
    if (!Number.isFinite(base)) return `₹${raw}K`;
    return formatMoney(base * 1000);
  });

  out = out.replace(/₹\s*([\d,]+(?:\.\d+)?)/g, (_, raw) => {
    const amount = Number(String(raw).replace(/,/g, ''));
    if (!Number.isFinite(amount)) return `₹${raw}`;
    return formatMoney(amount);
  });

  return out;
}
